import { useEffect, useMemo, useRef } from 'react';

import type { OutstandingApprovalItem } from '@time-tracking/shared';

import { useFocusTrap } from '../../hooks/useFocusTrap.js';

interface BulkApproveConfirmDialogProps {
  /** The selection awaiting confirmation; null = closed. */
  items: OutstandingApprovalItem[] | null;
  onCancel: () => void;
  onConfirm: (items: OutstandingApprovalItem[]) => void;
}

/**
 * Confirms a bulk approval large enough to be worth pausing over.
 *
 * A component rather than JSX inline in the page, which is what it was and why it was
 * broken. Every other dialog in this app is its own component and every one of them
 * calls `useFocusTrap`; this was the single exception, and it was correspondingly the
 * single dialog that declared `aria-modal="true"` while leaving focus out on the
 * obscured page behind it — Tab walked the page, Escape did nothing, and closing it
 * dropped focus on `<body>`.
 *
 * Worth naming: the axe pass over this view is clean and always was. axe cannot see
 * focus management, so an inline dialog is exactly the shape that slips through an
 * automated a11y gate — the same blind spot recorded in
 * docs/solutions/ui-bugs/interactive-tr-no-aria-role-missing-focus-trap-2026-05-07.md.
 * Being a component is what makes the guarantee structural instead of remembered.
 */
export function BulkApproveConfirmDialog({
  items,
  onCancel,
  onConfirm,
}: BulkApproveConfirmDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelBtnRef = useRef<HTMLButtonElement>(null);

  // Focus opens on Cancel, not Approve. This publishes to the cost-center export with
  // no undo, so a stray Enter on an unread dialog must not be the one that commits it.
  // Handed to the trap rather than focused separately — see its `initialFocusRef` note.
  useFocusTrap(dialogRef, items !== null, cancelBtnRef);

  useEffect(() => {
    if (!items) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [items, onCancel]);

  const weekCount = useMemo(
    () => (items ? new Set(items.map((i) => i.weekStartDate)).size : 0),
    [items],
  );

  if (!items) return null;

  return (
    <div className="dialog-backdrop" role="presentation">
      <div
        ref={dialogRef}
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-confirm-title"
      >
        <div className="dialog__head">
          <h3 id="bulk-confirm-title">Approve {items.length} timesheets?</h3>
        </div>
        <div className="dialog__body">
          <p>
            This publishes {items.length} sheets across {weekCount}{' '}
            {weekCount === 1 ? 'week' : 'weeks'} to the cost-center export immediately. There is no
            undo from this view.
          </p>
        </div>
        <div className="dialog__foot">
          <button ref={cancelBtnRef} type="button" className="btn btn--light" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn primary" onClick={() => onConfirm(items)}>
            Approve {items.length}
          </button>
        </div>
      </div>
    </div>
  );
}
